import { useCallback, useEffect, useMemo, useState } from 'react';

import { equipmentSortNickname } from '@@utils/sort';
import {
    InfiniteScroll,
    PageLoader,
    PrimaryButton,
    VerticalContainer
} from '@american-autonomy/rantizo-ui';

import AircraftCard from 'components/cards/AircraftCard';
import NoAircraftCard from 'components/cards/NoAircraftCard';

import useAircraft from 'hooks/useAircraft';
import useNavigation from 'hooks/useNavigation';
import usePageRoutes from 'hooks/usePageRoutes';
import useRoles from 'hooks/useRoles';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { Aircraft, AircraftApiResponse, FunctionComponent, Key, Props } from './types';

import styles from './styles.module.scss';

const AircraftList: FunctionComponent<Props> = ({ hideAddButton = false, testId = TEST_ID }) => {
    const { canManageEquipment } = useRoles();
    const { deleteAircraft, fetchAircrafts } = useAircraft();
    const { navigate } = useNavigation();
    const { organizationNewAircraft } = usePageRoutes();
    const [aircraft, setAircraft] = useState<AircraftApiResponse>({
        hasNext: false,
        nextPageToken: '',
        objects: []
    });
    const [loading, setLoading] = useState(true);
    const { BUTTON } = useTranslation();

    const fetchAircraftData = useCallback(
        async (hasMore: boolean, nextToken: string) => {
            const response = await fetchAircrafts(hasMore ? nextToken : undefined);

            const { hasNext, nextPageToken, objects: newAircraft } = response;

            setAircraft((currentState: AircraftApiResponse) => {
                const newDrones = [...currentState.objects, ...newAircraft];

                newDrones.sort(equipmentSortNickname);

                return { hasNext, nextPageToken, objects: newDrones };
            });
        },
        [fetchAircrafts, setAircraft]
    );

    const handleAddAircraft = useCallback(() => {
        navigate(organizationNewAircraft);
    }, [navigate, organizationNewAircraft]);

    const noAircraft = useMemo(() => aircraft.objects.length === 0, [aircraft.objects.length]);

    useEffect(() => {
        const initialAircraftFetch = async () => {
            await fetchAircraftData(false, '');
        };

        if (loading) {
            initialAircraftFetch();
            setLoading(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    const deleteAndRefresh = useCallback(
        async (id: string) => {
            await deleteAircraft(id);

            setAircraft((aircraftList: AircraftApiResponse) => ({
                hasNext: aircraftList.hasNext,
                nextPageToken: aircraftList.nextPageToken,
                objects: aircraftList.objects.filter(
                    (currentAircraft: Aircraft) => currentAircraft.id !== id
                )
            }));
        },
        [setAircraft, deleteAircraft]
    );

    return (
        <VerticalContainer className={styles.aircraftList} testId={testId}>
            {loading && <PageLoader />}

            {noAircraft && !loading && <NoAircraftCard />}

            {!noAircraft && (
                <InfiniteScroll
                    fetchNextPage={async () =>
                        await fetchAircraftData(aircraft.hasNext, aircraft.nextPageToken)
                    }
                    className={styles.list}
                    hasNextPage={aircraft.hasNext}
                    loader={<PageLoader />}
                    nextPageToken={aircraft.nextPageToken}
                >
                    {aircraft.objects.map((aircraft: Aircraft, index: Key | null | undefined) => (
                        <AircraftCard
                            aircraft={aircraft}
                            key={index}
                            onDelete={() => deleteAndRefresh(aircraft.id)}
                        />
                    ))}
                </InfiniteScroll>
            )}

            {canManageEquipment && !hideAddButton && (
                <PrimaryButton
                    className={styles.button}
                    onClick={handleAddAircraft}
                    text={BUTTON}
                />
            )}
        </VerticalContainer>
    );
};

export default AircraftList;
